import { Box, Stack, Typography } from '@mui/material';
import { CharacterGrade, SoundGrade } from '../../mockLessonContent';
import { ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, SLATE, SURFACE_SOFT, YELLOW } from '../../studentStyles';

const gradeColors: Record<SoundGrade, string> = { good: GREEN, ok: YELLOW, bad: RED };
const gradeLabels: Record<SoundGrade, string> = { good: 'Good', ok: 'Close', bad: 'Needs practice' };
const gradeSymbols: Record<SoundGrade, string> = { good: '✓', ok: '~', bad: '!' };

export function SoundIndicator({ part, value }: { part: string; value: SoundGrade }) {
    return <Box component="span" role="img" aria-label={`${part}: ${gradeLabels[value]}`} title={`${part}: ${gradeLabels[value]}`} sx={{ flex: 1, minWidth: 12, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: NAVY, borderBottom: `6px solid ${gradeColors[value]}`, borderRadius: '3px' }}>{gradeSymbols[value]}</Box>;
}

const legend = [
    { color: GREEN, label: 'Good', symbol: '✓' },
    { color: YELLOW, label: 'Close', symbol: '~' },
    { color: RED, label: 'Needs practice', symbol: '!' },
];

// Per-character initial/final/tone grades — the student-side echo of the
// teacher Speaking review grading (LessonSpeakingReviewPage).
export default function PronunciationDetail({ grades }: { grades: CharacterGrade[] }) {
    const needsWork = grades.some(g => g.initial !== 'good' || g.final !== 'good' || g.tone !== 'good');

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {grades.map((grade, index) => {
                    const alignment = grade.alignment ?? 'speech';
                    return (
                        <Box
                            key={`${grade.character}-${index}`}
                            sx={{
                                minWidth: 56, p: 1, borderRadius: '12px', backgroundColor: SURFACE_SOFT, textAlign: 'center',
                                border: alignment === 'speech' ? 'none' : `2px dashed ${alignment === 'silence' ? RED : SLATE}`,
                            }}
                        >
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, lineHeight: 1.2 }}>
                                {grade.character}
                            </Typography>
                            {/* Target pinyin, then what we heard directly under it. */}
                            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, mt: 0.25 }}>
                                {grade.pinyinExpected}
                            </Typography>
                            <Typography sx={{
                                fontFamily: INTER,
                                fontSize: 11,
                                fontWeight: grade.pinyinSpoken === grade.pinyinExpected ? 400 : 700,
                                color: grade.pinyinSpoken === grade.pinyinExpected ? SLATE : ERROR_TEXT,
                            }}>
                                {grade.pinyinSpoken}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '3px', mt: 0.75 }}>
                                {([['initial', grade.initial], ['final', grade.final], ['tone', grade.tone]] as const).map(([part, value]) => (
                                    <SoundIndicator key={part} part={part} value={value} />
                                ))}
                            </Box>
                            {alignment !== 'speech' && (
                                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: alignment === 'silence' ? ERROR_TEXT : SLATE, mt: 0.5 }}>
                                    {alignment === 'silence' ? 'not heard' : 'extra sound'}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>
                    Pinyin: target · heard
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>
                    Bars: Initial · Final · Tone
                </Typography>
                {legend.map(item => (
                    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="span" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: NAVY, borderBottom: `3px solid ${item.color}` }}>{item.symbol}</Box>
                        <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>{item.label}</Typography>
                    </Box>
                ))}
            </Stack>
            {needsWork && (
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, textAlign: 'center', mt: 1.5 }}>
                    Tip: practice the sounds marked Close or Needs practice — slow is fine.
                </Typography>
            )}
        </Box>
    );
}
